import React, { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { BrandName } from "./BrandName";
import { ArrowLeft, ArrowRight, PartyPopper, Loader2 } from "lucide-react";
import {
  journeyOptions,
  pathOptions,
  partnerOptions,
  timelineOptions,
  homeTypeOptions,
  bedroomOptions,
  bathroomOptions,
  livingOptions,
} from "../mock";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

import { OptionList, PillGroup, PriceSlider, LocationFields, ContactFields } from "./WizardFields";
import { toast } from "sonner";

const initialForm = {
  journey: "",
  path: "",
  hasPartner: "",
  timeline: "",
  state: "",
  city: "",
  homePrice: 350000,
  homeType: "",
  bedrooms: "",
  bathrooms: "",
  livingSituation: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const buildSteps = (form, set) => {
  const isInvestor = form.journey === "investor";
  return [
    {
      title: "Where are you in your homeownership journey?",
      valid: () => !!form.journey,
      body: <OptionList prefix="journey" options={journeyOptions} value={form.journey} onChange={(v) => set("journey", v)} />,
    },
    {
      title: <>How do you want to <BrandName />?</>,
      valid: () => !!form.path,
      body: <OptionList prefix="path" options={pathOptions} value={form.path} onChange={(v) => set("path", v)} />,
    },
    {
      title: "Do you already have a co-buyer or investor in mind?",
      valid: () => !!form.hasPartner,
      body: (
        <div className="space-y-3">
          <OptionList prefix="partner" options={partnerOptions} value={form.hasPartner} onChange={(v) => set("hasPartner", v)} />
          <p className="pt-1 text-sm text-slate-500">
            Don't know a buyer or investor? No problem — join the DubbleUpp Community and our team
            will help find a partner for you.
          </p>
        </div>
      ),
    },
    {
      title: "When could you see yourself making a move?",
      subtitle: "No pressure — this just helps us pace your plan.",
      valid: () => !!form.timeline,
      body: <OptionList prefix="timeline" options={timelineOptions} value={form.timeline} onChange={(v) => set("timeline", v)} />,
    },
    {
      title: isInvestor ? "Where would you like to invest?" : "Where are you buying?",
      subtitle: "We'll connect you with the right local team.",
      valid: () => !!form.state && form.city.trim().length > 1,
      body: <LocationFields form={form} set={set} />,
    },
    {
      title: isInvestor ? "What price range are you looking to invest in?" : "What's your starting home price?",
      subtitle: "Slide to your approximate range.",
      valid: () => true,
      body: <PriceSlider value={form.homePrice} onChange={(v) => set("homePrice", v)} />,
    },
    {
      title: "What type of home are you looking for?",
      skip: isInvestor,
      valid: () => !!form.homeType,
      body: (
        <OptionList
          prefix="hometype"
          options={homeTypeOptions.map((t) => ({ id: t.toLowerCase().replace(/[^a-z]/g, ""), label: t }))}
          value={form.homeType}
          onChange={(v) => set("homeType", v)}
        />
      ),
    },
    {
      title: "How many bedrooms and bathrooms?",
      skip: isInvestor,
      valid: () => !!form.bedrooms && !!form.bathrooms,
      body: (
        <div className="space-y-6">
          <PillGroup prefix="beds" label="Bedrooms" options={bedroomOptions} value={form.bedrooms} onChange={(v) => set("bedrooms", v)} />
          <PillGroup prefix="baths" label="Bathrooms" options={bathroomOptions} value={form.bathrooms} onChange={(v) => set("bathrooms", v)} />
        </div>
      ),
    },
    {
      title: "What best describes your current living situation?",
      skip: isInvestor,
      valid: () => !!form.livingSituation,
      body: <OptionList prefix="living" options={livingOptions} value={form.livingSituation} onChange={(v) => set("livingSituation", v)} />,
    },
    {
      title: "Almost there — how can we reach you?",
      valid: () =>
        form.firstName.trim() && form.lastName.trim() && emailOk(form.email) && form.phone.trim().length >= 7,
      body: <ContactFields form={form} set={set} />,
    },
  ].filter((s) => !s.skip);
};

const GetStartedModal = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const steps = buildSteps(form, set);
  const current = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;

  const reset = () => {
    setStep(0);
    setForm(initialForm);
    setDone(false);
    setSubmitting(false);
  };

  const handleOpenChange = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/leads`, form);
      setDone(true);
      toast.success("You're in! Our team will reach out shortly.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto rounded-3xl p-0 sm:max-w-lg">
        {done ? (
          <div className="flex flex-col items-center px-8 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF9EA]">
              <PartyPopper className="h-8 w-8 text-[#25862F]" />
            </span>
            <h3 data-testid="lead-success-title" className="mt-6 font-heading text-2xl font-extrabold text-slate-900">
              Nice move, {form.firstName}!
            </h3>
            <p className="mt-3 max-w-sm text-slate-600">
              You're on your way. A DubbleUpp expert will reach out shortly to help you make big
              moves, together.
            </p>
            <Button
              data-testid="lead-done-btn"
              onClick={() => handleOpenChange(false)}
              className="mt-8 rounded-full bg-[#3CC54F] px-8 py-3 font-semibold text-white hover:bg-[#25862F]"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="px-6 py-8 sm:px-8">
            <div className="mb-6 flex items-center gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                    i <= step ? "bg-[#3CC54F]" : "bg-slate-200"
                  }`}
                />
              ))}
            </div>
            <p data-testid="wizard-step-indicator" className="text-xs font-bold uppercase tracking-[0.14em] text-[#25862F]">
              Step {step + 1} of {steps.length}
            </p>
            <h3 data-testid="wizard-step-title" className="mt-2 font-heading text-2xl font-extrabold leading-snug text-slate-900">
              {current.title}
            </h3>
            {current.subtitle && <p className="mt-1.5 text-sm text-slate-500">{current.subtitle}</p>}
            <div className="mt-6">{current.body}</div>

            <div className="mt-8 flex items-center justify-between">
              <button
                data-testid="wizard-back-btn"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors duration-200 hover:text-slate-800 disabled:invisible"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {!isLast ? (
                <Button
                  data-testid="wizard-continue-btn"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!current.valid()}
                  className="rounded-full bg-[#3CC54F] px-7 py-2.5 font-semibold text-white hover:bg-[#25862F] disabled:opacity-40"
                >
                  Continue <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  data-testid="wizard-submit-btn"
                  onClick={handleSubmit}
                  disabled={!current.valid() || submitting}
                  className="rounded-full bg-[#3CC54F] px-7 py-2.5 font-semibold text-white hover:bg-[#25862F] disabled:opacity-40"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    "Get Started"
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GetStartedModal;
